import Service from '@ember/service';
import { task } from 'ember-concurrency';

import config from 'later/config/environment';
import { DEFAULT_PRODUCT_FETCH_LIMIT } from 'later/utils/constants';
import graphCall from 'later/utils/graph-call';

import type { GraphCallParams } from '../instagram-graph';
import type {
  FacebookComment,
  FacebookCommentResponse,
  FacebookConversation,
  FacebookConversationResponse,
  FacebookPostResponse,
  FacebookProfileResponse
} from 'shared/types/facebook';

export default class FacebookGraphService extends Service {
  facebookGraphVersion = config.APP.facebookGraphVersion;

  /**
   * List of post fields to be returned from fetch media requests
   */
  postFields = [
    'message',
    'created_time',
    'from',
    'picture',
    'full_picture',
    'permalink_url',
    'type',
    'source',
    'reactions'
  ];

  /**
   * List of comment fields to be returned from fetch comment requests
   */
  commentFields = ['message', 'from', 'created_time', 'parent', 'reactions'];

  /**
   * List of message fields to be returned from fetch message requests
   */
  messageFields = ['message', 'from', 'attachments', 'created_time', 'is_unsupported'];

  fetchPage = task(async (token: string, pageId: string) => {
    const result = await graphCall(
      `/${encodeURIComponent(pageId)}`,
      { fields: 'name,business' },
      {
        method: 'get',
        token
      }
    );
    return result;
  });

  fetchProduct = task(async (token: string | undefined, productId: string, businessAccountToken: string) => {
    const fields = ['image_url', 'name'];
    const result = await graphCall(
      `/${productId}`,
      { fields: fields.join(',') },
      {
        method: 'get',
        token,
        backupToken: businessAccountToken
      }
    );

    return result;
  });

  fetchProductTagEligibility = task(
    async (instagramUserId: string, token: string | undefined, businessAccountToken: string) => {
      return await graphCall(
        `/${encodeURIComponent(instagramUserId)}`,
        { fields: 'shopping_product_tag_eligibility' },
        {
          method: 'get',
          token,
          backupToken: businessAccountToken
        }
      );
    }
  );

  fetchAvailableCatalogs = task(
    async (instagramUserId: string, token: string | undefined, businessAccountToken: string) => {
      const result = await graphCall(
        `/${encodeURIComponent(instagramUserId)}/available_catalogs`,
        {},
        {
          method: 'get',
          token,
          backupToken: businessAccountToken
        }
      );

      return result;
    }
  );

  fetchCatalogProducts = task(
    async ({
      instagramUserId,
      token,
      params,
      paginationUrl,
      businessAccountToken
    }: {
      instagramUserId: string;
      token?: string;
      params: Record<string, unknown>;
      paginationUrl?: string;
      businessAccountToken: string;
    }) => {
      const catalogProductsUrl = paginationUrl || `/${encodeURIComponent(instagramUserId)}/catalog_product_search`;
      const result = await graphCall(
        catalogProductsUrl,
        { ...params, limit: DEFAULT_PRODUCT_FETCH_LIMIT },
        {
          method: 'get',
          token,
          backupToken: businessAccountToken
        }
      );

      return result;
    }
  );

  async fetchProfileById(
    token: string,
    profileId: string,
    params?: Record<string, unknown>
  ): Promise<FacebookProfileResponse> {
    const result = await graphCall(`/${encodeURIComponent(profileId)}?fields=name,picture`, params, {
      method: 'get',
      token
    });
    return result;
  }

  async fetchConversations(
    token: string,
    pageId: string,
    messageLimit?: number,
    params?: Record<string, unknown>
  ): Promise<FacebookConversationResponse> {
    const conversationFields = this.buildConversationFields(messageLimit);
    const result = await graphCall(
      `/${encodeURIComponent(pageId)}/conversations`,
      { ...params, fields: conversationFields.join(',') },
      {
        method: 'get',
        token
      }
    );
    if (params && params.platform) {
      result.platform = params.platform;
    }
    return result;
  }

  async fetchConversationById(
    token: string,
    conversationId: string,
    messageLimit?: number,
    params?: Record<string, unknown>
  ): Promise<FacebookConversation> {
    return await graphCall(
      `/${encodeURIComponent(conversationId)}`,
      { ...params, fields: this.buildConversationFields(messageLimit).join(',') },
      {
        method: 'get',
        token
      }
    );
  }

  async sendMessageToConversation(
    token: string,
    pageId: string,
    recipientId: string,
    message: string
  ): Promise<{
    message_id: string;
  }> {
    const params = {
      recipient: { id: recipientId },
      message: { text: message },
      messaging_type: 'MESSAGE_TAG',
      tag: 'HUMAN_AGENT'
    };
    return await graphCall(`/${encodeURIComponent(pageId)}/messages`, { ...params }, { method: 'post', token });
  }

  async fetchPosts(token: string, pageId: string): Promise<FacebookPostResponse> {
    const mediaFields = ['media_type', 'url', 'description', 'type', 'media'].join(',');
    const commentFields = ['message', 'comment_count', 'from', 'attachment', 'created_time', 'is_hidden'].join(',');
    const postFields = [
      'message',
      'parent_id',
      'updated_time',
      `comments{${`comments{comments,${commentFields}}`},${commentFields}}`, // Nested comments of comments
      `attachments{${mediaFields}}`
    ].join(',');

    return await graphCall(`/${encodeURIComponent(pageId)}/feed?fields=${postFields}`, {}, { token });
  }

  async fetchComments(token: string, pagePostId: string): Promise<FacebookCommentResponse> {
    const fields = ['from', 'message'].join(',');
    return await graphCall(`/${encodeURIComponent(pagePostId)}/comments?fields=${fields}`, {}, { token });
  }

  async postComment(token: string, pagePostId: string, message: string): Promise<FacebookComment> {
    const { id } = await graphCall(
      `/${encodeURIComponent(pagePostId)}/comments`,
      { message },
      { method: 'post', token }
    );
    return await graphCall(`/${id}`, {}, { token });
  }

  graphRequest<T = unknown>(
    endpoint: string,
    token: string,
    params?: GraphCallParams,
    bodyParams?: Record<string, unknown>
  ): Promise<T> {
    return graphCall(endpoint, bodyParams, { ...params, token });
  }

  buildConversationFields(messageLimit?: number): string[] {
    return [
      'participants',
      `messages${messageLimit ? `.limit(${messageLimit})` : ''}{${this.messageFields.join(',')}}`,
      'can_reply',
      'updated_time'
    ];
  }
}

declare module '@ember/service' {
  interface Registry {
    'social/facebook-graph': FacebookGraphService;
  }
}
